import React, { useRef, useEffect } from 'react';

const TrustBenefitsBar = () => {
  const benefits = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
      title: "Free Delivery",
      description: "On orders above Kes 6,000",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Genuine Products",
      description: "100% Authentic",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: "Secure Payment",
      description: "Safe & Secure Checkout",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
      ),
      title: "Easy Returns",
      description: "30-day return policy",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l4-8 4 4 4-6 4 4" />
        </svg>
      ),
      title: "Return on Investment",
      description: "We value your money",
    },
  ];

  const trackRef = useRef(null);
  const copiesCount = Math.max(4, benefits.length * 2);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let animationId;
    let position = 0;
    const speed = 0.5;

    const isMobile = () => window.innerWidth < 768;

    const getSingleCopyWidth = () => {
      const totalChildren = track.children.length;
      const singleCount = Math.ceil(totalChildren / copiesCount);
      if (totalChildren === 0) return 0;
      const itemWidth = track.scrollWidth / totalChildren;
      return itemWidth * singleCount;
    };

    const step = () => {
      if (!isMobile()) {
        track.style.transform = 'translateX(0)';
        animationId = requestAnimationFrame(step);
        return;
      }
      position += speed;
      const singleWidth = getSingleCopyWidth();
      if (singleWidth > 0 && position >= singleWidth) {
        position = position - singleWidth;
      }
      track.style.transform = `translateX(-${position}px)`;
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);

    const pause = () => cancelAnimationFrame(animationId);
    const resume = () => { animationId = requestAnimationFrame(step); };

    track.parentElement.addEventListener('mouseenter', pause);
    track.parentElement.addEventListener('mouseleave', resume);
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('touchend', resume);

    const handleResize = () => {
      if (!isMobile()) {
        cancelAnimationFrame(animationId);
        track.style.transform = 'translateX(0)';
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      track.parentElement.removeEventListener('mouseenter', pause);
      track.parentElement.removeEventListener('mouseleave', resume);
      track.removeEventListener('touchstart', pause);
      track.removeEventListener('touchend', resume);
      window.removeEventListener('resize', handleResize);
    };
  }, [copiesCount]);

  return (
    <div className="bg-gold border-b">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Desktop: single row, no duplicates */}
        <div className="hidden md:grid md:grid-cols-5 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="flex items-center gap-4"
            >
              <div className="text-black flex-shrink-0">{benefit.icon}</div>
              <div>
                <p className="font-semibold text-sm text-white">{benefit.title}</p>
                <p className="text-xs text-white">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: infinite-scroll with duplicates */}
        <div
          className="flex md:hidden overflow-hidden"
          style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
          }}
        >
          <div
            ref={trackRef}
            className="flex gap-6 w-max"
            style={{ willChange: 'transform' }}
          >
            {Array.from({ length: copiesCount }).flatMap((_, copyIndex) =>
              benefits.map((benefit, index) => (
                <div
                  key={`${copyIndex}-${index}`}
                  className="flex-shrink-0 w-[180px] flex items-center gap-4"
                >
                  <div className="text-black flex-shrink-0">{benefit.icon}</div>
                  <div>
                    <p className="font-semibold text-sm text-white">{benefit.title}</p>
                    <p className="text-xs text-white">{benefit.description}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustBenefitsBar;
